/* eslint-disable */
import { ESETTINGS_KEYS, IUserInitialPosition, IUserSetting, IUserSpeedData } from "~/state/user/interfaces";
import { CONFIG } from "~/config";
import { getMapsetSeoById } from "~/util/mapset";
import { frpc } from "../general";
import { ICreateLikeData, IMapyBoxItem } from "~/interfaces";

interface IFolderData {
	id: string;
	title: string;
}

interface ILikeCopyOutput {
	copied: Array<{
		new_id: string;
		old_id: string;
	}>;
	status: number;
	statusMessage: string;
}

export interface IUserSettingsResponse {
	items?: Array<IUserSetting>;
	update_time?: number,
	status: number,
	statusMessage: string;
}

export const OPTIONS = {
	// v [ms]
	folderDuration: 30000,
	baseDuration: 30000,
	url: "/mapybox-ng/",
};

const DEBUG = false;

// 32 znaku hash orig
function generateId() {
	return Math.random().toString(16).replace("0.", "");
}

export function prepareSetting(setting: ESETTINGS_KEYS, value: boolean | number | string | IUserInitialPosition | Array<any> | IUserSpeedData): Pick<IUserSetting, "title" | "data" | "type"> {
	let data = {
		value: undefined,
		type: undefined,
	};

	switch (typeof value) {
		case "boolean":
			data = {
				value,
				type: "boolean",
			};
			break;

		case "string":
			data = {
				value,
				type: "string",
			};
			break;

		case "number":
			data = {
				value,
				type: "number",
			};
			break;

		case "object":
			if (Array.isArray(value)) {
				data = {
					value,
					type: "list",
				};
			} else {
				data = {
					value,
					type: "object",
				};
			}
			break;

		// tohle by nemelo nikdy nastat
		default:
			data = {
				value: value as any,
				type: "object",
			};
	}

	return {
		title: setting,
		data,
		type: "settings",
	};
}

export function setMapsetUserSettings(mapsetId: number, overlays?: Array<number>): Promise<void> {
	if (CONFIG.mapsetsBlackList.indexOf(getMapsetSeoById(mapsetId)) === -1) {
		const settingsMapset = prepareSetting(ESETTINGS_KEYS.MAPSET_WEB_LAST_ID, mapsetId);
		const settings = [settingsMapset];

		if (overlays) {
			settings.push(prepareSetting(ESETTINGS_KEYS.MAPSET_WEB_OVERLAYS, overlays));
		}

		return setUserSettings(settings);
	}

	return Promise.resolve();
}

export function setOverlaysUserSettings(overlays: Array<number>): Promise<void> {
	const settings = [prepareSetting(ESETTINGS_KEYS.MAPSET_WEB_OVERLAYS, overlays)];

	return setUserSettings(settings);
}

/**
 * Seznam uzivatelskeho hodnoceni.
 */
export function getUserSettings(): Promise<IUserSettingsResponse> {
	return frpc<IUserSettingsResponse>({
		url: CONFIG.requestUrl + OPTIONS.url,
		method: "settings.get",
		args: [Object.values(ESETTINGS_KEYS)],
	}).promise;
}

/**
 * Nastaveni uzivatelskeho hodnoceni.
 */
export function setUserSettings(newItem: Array<Pick<IUserSetting, "title" | "data">> | Pick<IUserSetting, "title" | "data">): Promise<void> {
	const settings = Array.isArray(newItem) ? newItem : [newItem];
	const hints = {};

	settings.forEach((setting, index) => {
		if (setting.title === ESETTINGS_KEYS.MAP_POSITION_WEB_DEFAULT) {
			const value = setting?.data?.value as IUserInitialPosition;

			if (value?.mark) {
				hints[`0.${index}.data.value.mark.lon`] = "float";
				hints[`0.${index}.data.value.mark.lat`] = "float";
			}

			// pracujeme s null, ale BE chce prazdny objekt
			if (value === null) {
				setting.data.value = {};
			}
		} else if (setting.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED) {
			for (const criterion in settings[0].data.value as object) {
				hints[`0.${index}.data.value.${criterion}.value`] = "float";
			}
		}
	});

	return frpc<null>({
		url: CONFIG.requestUrl + OPTIONS.url,
		method: "settings.set",
		args: [settings],
		hints,
	}).promise;
}

interface ILikeHints {
	[key: string]: string;
}

interface ICreateLikeOpts {
	duplicity?: "check" | "ignore";
}

interface ILikeMoveOpts {
	actualFolderId?: string;
}

function debugCover(cb: () => Promise<any>, debugName: string, debugOutput: any, debugObj: any) {
	console.log([debugName, { debugObj, debugOutput }]);

	return DEBUG
		? Promise.resolve(debugOutput)
		: cb();
}

// nevraci data
export function likeUpdate(id = "", like: Partial<ICreateLikeData> = {}, hints: ILikeHints = {}): Promise<IMapyBoxItem> {
	return debugCover(() => Mapy.ProviderMapyBox.likeUpdate(id, like, hints), "likeUpdate", {
		id, ...like
	}, {
		id,
		like,
		hints,
	});
}

export function likeCreate(like: Partial<ICreateLikeData> = {}, hints: ILikeHints = {}, opts: ICreateLikeOpts = {}): Promise<IMapyBoxItem> {
	return debugCover(() => Mapy.ProviderMapyBox.likeCreate(like, hints, opts), "likeCreate", like, {
		like,
		hints,
	});
}

export function likeMove(ids: Array<string> | string, parentId = "", opts: ILikeMoveOpts = {}): Promise<any> {
	return debugCover(() => Mapy.ProviderMapyBox.likeMove(ids, parentId, opts), "likeMove", {
		ids,
		parentId,
	}, {
		ids,
		parentId,
		opts,
	});
}

export function folderCreate(title = "", parentId = ""): Promise<IMapyBoxItem> {
	return debugCover(() => Mapy.ProviderMapyBox.folderCreate(title, parentId), "folderCreate", {
		id: generateId(),
		title,
		parentId,
	}, {
		title,
		parentId,
	});
}

// nevraci data
export function folderUpdate(id = "", folder: any = {}, hints = {}): Promise<IMapyBoxItem> {
	return debugCover(() => Mapy.ProviderMapyBox.folderUpdate(id, folder, hints), "folderUpdate", {
		id,
		...folder,
	}, {
		id,
		folder,
		hints,
	});
}

export function trackUpdate(tid = "", data: Partial<ICreateLikeData>): Promise<IMapyBoxItem> {
	return debugCover(() => Mapy.ProviderMapyBox.trackUpdate(tid, data), "trackUpdate", {
		id: tid,
		...data,
	}, {
		tid,
		data,
	});
}

export function trackLink(id = "", parent_id = "", usertitle = "", description = ""): Promise<IMapyBoxItem> {
	return debugCover(() => Mapy.ProviderMapyBox.trackLink(id, parent_id, usertitle, description), "trackLink", {
		id,
		parent_id,
		usertitle,
		description,
	}, {
		id,
		parent_id,
		usertitle,
		description,
	});
}

export function trackCopy(id = "", parent_id = "", usertitle = "", description = ""): Promise<ILikeCopyOutput> {
	return debugCover(() => Mapy.ProviderMapyBox.trackCopy(id, parent_id, usertitle, description), "trackCopy", {
		id,
		parent_id,
		usertitle,
		description
	}, {
		id,
		parent_id,
		usertitle,
		description,
	});
}

export function likeCopy(ids: Array<string>, parentId = ""): Promise<ILikeCopyOutput> {
	return debugCover(() => Mapy.ProviderMapyBox.likeCopy(ids, parentId), "likeCopy", {
		ids,
		parentId,
		copied: [{ new_id: ids[0] }],
	}, {
		ids,
		parentId,
		copied: [{ new_id: ids[0] }],
	});
}

export function folderCopy(id = "", parentId = "", updateObj = {}): Promise<IMapyBoxItem> {
	return debugCover(() => Mapy.ProviderMapyBox.folderCopy(id, parentId, updateObj), "folderCopy", {
		id,
		parentId,
		updateObj,
	}, {
		id,
		parentId,
		updateObj,
	});
}

/* sdilena slozka */
let folderData: IFolderData = null;

export function getFolderData() {
	return folderData;
}

export function setFolderData(newFolderData: IFolderData) {
	folderData = newFolderData;
}
